import React, { useCallback, useState } from "react";
import { observer } from "mobx-react-lite";
import { FormikErrors } from "formik";

import { translationService } from "../../../services/translation/TranslationService";
import SuggestionBox from "../../../components/suggestion-box/SuggestionBox";
import FormField from "../../../components/form-field/FormField";
import { NameEntity } from "../../../helpers/NameEntity";
import useErrorHandler, {
  ApplicationError,
} from "../../../services/error-handler/ErrorHandlerService";
import UIObject from "../../../models/UIObject";
import { RequiredValidatorModel } from "../../../services/form/validators/required/RequiredValidator";
import { ProjectsStorageModel } from "../../../services/project/ProjectsStorage";
import { api } from "../../../services/api/ApiService";
import { Dropdown, DropdownOption } from "../../../components/dropdown/Dropdown";
import { JobStatus, JobType, JobPriority } from "../../../generated/Jobs";

export function EditJobName(props: any) {
  const [requiredValidator] = useState(() => RequiredValidatorModel.create({}));

  return (
    <FormField
      id="job-name"
      className="input"
      labelKey="jobs.EditJobName.label"
      required
      component="input"
      fieldName="name"
      fieldValue={props.name}
      validator={requiredValidator}
      placeholderKey="jobs.EditJobName.placeholder"
      maxLength={100}
      errorName="name"
    />
  );
}

interface EditProjectProps {
  project: NameEntity;
  setFieldValue: (field: string, value: any) => Promise<void | FormikErrors<any>>;
  disabled?: boolean;
}

export function EditProject(props: EditProjectProps) {
  const { setFieldValue } = props;
  const { handleErrorInSnackbar } = useErrorHandler();

  const [requiredValidator] = useState(() => RequiredValidatorModel.create({}));
  const [projectsService] = useState(() =>
    ProjectsStorageModel.create({ autoLoad: false }, { api })
  );

  const handleSearchProjectChange = useCallback(
    async (searchText: string): Promise<UIObject[]> => {
      try {
        const results = await projectsService.getFilteredProjects(searchText);
        return results;
      } catch (error) {
        handleErrorInSnackbar(error as ApplicationError);
        return [];
      }
    },
    [handleErrorInSnackbar, projectsService]
  );

  const clearProjectFieldValue = (text: string) => {
    if (!text) setFieldValue("project", "");
  };

  return (
    <FormField
      id="project"
      labelKey="general.in-project"
      fieldName="project"
      fieldValue={props.project}
      required
      validator={requiredValidator}
      children={() => (
        <SuggestionBox
          defaultSelectedItem={props.project && props.project.id !== 0 ? props.project : undefined}
          onSearchTextChange={(searchText) => handleSearchProjectChange(searchText)}
          searchTextChanged={clearProjectFieldValue}
          selectedItemChanged={(project: NameEntity | undefined) =>
            setFieldValue("project", project ? project : null)
          }
          placeholderKey="jobs.EditProject.add-project-name"
          noResultsFoundKey="general.projects.not-found"
          disabled={props.disabled}
          clearCurrentItem={() => clearProjectFieldValue("")}
        />
      )}
      errorName="project"
    />
  );
}

interface EditJobStatusProps {
  status: JobStatus;
  setFieldValue: (field: string, value: any) => Promise<void | FormikErrors<any>>;
}

export const EditJobStatus = observer((props: EditJobStatusProps) => {
  const { status, setFieldValue } = props;

  const optionsList: DropdownOption[] = Object.keys(JobStatus)
    .filter((status: any) => isNaN(status))
    .map((status) => {
      return {
        id: JobStatus[status],
        name: translationService.translate(`general.jobs.status.${status}`),
      };
    });

  return (
    <FormField
      id="status"
      className="select"
      labelKey="jobs.EditJobStatus.label"
      required
      fieldName="status"
      fieldValue={status}
      errorName="status"
      children={() => (
        <Dropdown
          selectedOption={optionsList.find((o) => o.id === status) ?? { id: "", name: "" }}
          options={optionsList}
          fieldName="status"
          setFieldValue={(field: string, value: any) => {
            setFieldValue(field, value.id);
          }}
        />
      )}
    />
  );
});

interface EditJobTypeProps {
  type: JobType;
  setFieldValue: (field: string, value: any) => Promise<void | FormikErrors<any>>;
}

export const EditJobType = observer((props: EditJobTypeProps) => {
  const { type, setFieldValue } = props;

  const optionsList: DropdownOption[] = Object.keys(JobType)
    .filter((type: any) => isNaN(type))
    .map((type) => {
      return {
        id: JobType[type],
        name: translationService.translate(`general.jobs.type.${type}`),
      };
    });

  return (
    <FormField
      id="type"
      className="select"
      labelKey="jobs.EditJobType.label"
      required
      fieldName="type"
      fieldValue={type}
      errorName="type"
      children={() => (
        <Dropdown
          selectedOption={optionsList.find((o) => o.id === type) ?? { id: "", name: "" }}
          options={optionsList}
          fieldName="type"
          setFieldValue={(field: string, value: any) => {
            setFieldValue(field, value.id);
          }}
        />
      )}
    />
  );
});

interface EditPriorityProps {
  priority: JobType;
  setFieldValue: (field: string, value: any) => Promise<void | FormikErrors<any>>;
}

export const EditPriority = observer((props: EditPriorityProps) => {
  const { priority, setFieldValue } = props;

  const optionsList: DropdownOption[] = Object.keys(JobPriority)
    .filter((priority: any) => isNaN(priority))
    .map((priority) => {
      return {
        id: JobPriority[priority],
        name: translationService.translate(`general.jobs.priority.${priority}`),
      };
    });

  return (
    <FormField
      id="priority"
      className="select"
      labelKey="general.priority"
      required
      fieldName="priority"
      fieldValue={priority}
      errorName="priority"
      children={() => (
        <Dropdown
          selectedOption={optionsList.find((o) => o.id === priority) ?? { id: "", name: "" }}
          options={optionsList}
          fieldName="priority"
          setFieldValue={(field: string, value: any) => {
            setFieldValue(field, value.id);
          }}
        />
      )}
    />
  );
});

export function DisplayName(props: any) {
  return (
    <FormField id="job-name" labelKey="jobs.DisplayName.label" readOnly fieldValue={props.name} />
  );
}

interface Project {
  project: NameEntity | null;
}

export function DisplayProject(props: Project) {
  return (
    <FormField
      id="project"
      labelKey="general.in-project"
      readOnly
      fieldValue={(props.project && props.project.name) || "-"}
    />
  );
}
interface Status {
  status: number;
}

export const DisplayStatus = observer((props: Status) => {
  return (
    <FormField
      id="status"
      labelKey="jobs.DisplayStatus.label"
      readOnly
      fieldValue={translationService.translate(`general.jobs.status.${JobStatus[props.status]}`)}
    />
  );
});

interface Type {
  type: number;
}

export const DisplayType = observer((props: Type) => {
  return (
    <FormField
      id="type"
      labelKey="jobs.DisplayType.label"
      readOnly
      fieldValue={translationService.translate(`general.jobs.type.${JobType[props.type]}`)}
    />
  );
});

interface Priority {
  priority: number;
}

export const DisplayPriority = observer((props: Priority) => {
  return (
    <FormField
      id="priority"
      readOnly
      labelKey="general.priority"
      fieldValue={translationService.translate(
        `general.jobs.priority.${JobPriority[props.priority]}`
      )}
    />
  );
});
